import { useShallow } from 'zustand/react/shallow';

import { useLagoon } from '../../hooks/useLagoon';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestState } from '../../store/useGuestStore/guestStore';
import { SIGN_IN_PARAM } from '../Constants';

interface Configuration {
  lob: string;
  coverage: string;
  signinUrl: string;
  brandmarkAffiliate: string;
  brandmarkVariant: string;
  languages: string;
}

interface HeaderData {
  lob: string;
  coverage: string;
  signinUrl: string;
  brandmarkAffiliate: string;
  brandmarkVariant: string;
  languages: string;
}

export const getHeaderContent = (
  configuration: Configuration[],
  lob: string,
  coverage: string
): HeaderData | undefined => {
  if (!lob && !coverage) {
    return undefined;
  }

  // Filter by LOB first
  const lobMatches = configuration?.filter((content) => content.lob === lob);
  if (!lobMatches || lobMatches.length === 0) {
    return undefined;
  }
  // Look for specific coverage match first
  const specificMatch = lobMatches.find((content) => {
    if (!content.coverage) {
      return false;
    }
    const coverageList = content.coverage.split(',').map((c) => c.trim());
    return content.coverage === coverage || coverageList?.includes(coverage);
  });

  if (specificMatch) {
    return specificMatch;
  }

  // Fall back to ALL coverage
  const allMatch = lobMatches.find((content) => content.coverage === 'ALL');
  return allMatch;
};

// Hook to get portal sign-in data that determines whether to show sign-in buttons and which route to use
export const usePortalSignInData: () => {
  shouldShowSignIn: boolean;
  shouldUsePortalSignInRoute: boolean;
  portalSignInRoute: string | null;
} = () => {
  const lagoonGuestLoginRoutes = useLagoon('guest-login-routes')();

  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );

  const { portal, signIn } = guestPlanSelectorData || {};

  const lagoonPortalRoutesObject = lagoonGuestLoginRoutes.find(
    (l) => l?.incomingPortal && l?.incomingPortal.toLowerCase() === portal?.toLowerCase()
  );

  const shouldHonorSignInParam = lagoonPortalRoutesObject != null;

  const shouldUsePortalRoute =
    shouldHonorSignInParam && signIn === SIGN_IN_PARAM.ENABLE;

  
  const shouldShowSignIn =
    signIn === SIGN_IN_PARAM.DISABLE
      ? false
      : shouldUsePortalRoute || !shouldHonorSignInParam;

  return {
    shouldShowSignIn,
    portalSignInRoute: lagoonPortalRoutesObject?.routeTo,
    shouldUsePortalSignInRoute: shouldUsePortalRoute,
  };
};
